/**
 * MIQCalibrationBanner Component
 * 
 * Shows in-app banners for MIQ calibration milestones:
 * 
 * 1. Countdown Banner (Day 25-29):
 *    "Your MIQ Baseline unlocks in X days. Keep logging meals to strengthen your model."
 *    - Brand gradient, non-blocking
 * 
 * 2. Day 30 Baseline Callout (calibration day >= 30 AND baseline_viewed_at is null):
 *    "Your MIQ Baseline is ready. View your personalized intelligence report."
 *    - Genius: "View Baseline Report" button
 *    - Non-Genius: "Upgrade to Genius Plaite" button
 *    - Persists until baseline_viewed_at is set
 */

import React from 'react';
import { Sparkles, ChevronRight, Lock, Zap } from 'lucide-react';
import { brand } from '../../styles/brand';
import { track } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

/**
 * Countdown Banner - Shows Day 25-29
 * Non-blocking, brand gradient
 * Shows trial ending warning for users on free trial
 */
export const MIQCountdownBanner = ({ daysRemaining, onNavigateToMIQ, isSignupTrial }) => {
  if (daysRemaining < 1 || daysRemaining > 5) return null;
  
  return (
    <div
      data-testid="miq-countdown-banner"
      onClick={() => {
        track('miq_countdown_banner_clicked', { days_remaining: daysRemaining });
        onNavigateToMIQ?.();
      }}
      style={{
        background: gradients.primary,
        padding: `${spacing.s3} ${spacing.s4}`,
        borderRadius: radius.md,
        marginBottom: spacing.s3,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3,
        boxShadow: shadows.glow,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = shadows.glow;
      }}
    >
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Sparkles size={18} color={colors.text} />
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: typography.body.fontSize,
          fontWeight: '600',
          color: colors.text,
          marginBottom: '2px'
        }}>
          Your MIQ Baseline unlocks in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
        </div>
        <div style={{
          fontSize: typography.bodySmall.fontSize,
          color: 'rgba(255, 255, 255, 0.85)'
        }}>
          {isSignupTrial 
            ? `Free Genius trial ends then — upgrade to keep full access`
            : `Keep logging meals to strengthen your model.`}
        </div>
      </div>
      
      <ChevronRight size={20} color="rgba(255, 255, 255, 0.7)" />
    </div>
  );
};

/**
 * Day 30 Baseline Callout - Shows when calibration complete and baseline not viewed
 * Prominent callout with action button
 */
export const MIQBaselineCallout = ({ 
  isGeniusTier, 
  onViewBaseline, 
  onUpgrade,
  onNavigateToMIQ 
}) => {
  const handleAction = () => {
    if (isGeniusTier) {
      track('miq_baseline_callout_view_clicked');
      onViewBaseline?.();
    } else {
      track('miq_baseline_callout_upgrade_clicked');
      onUpgrade?.();
    }
  };

  return (
    <div
      data-testid="miq-baseline-callout"
      style={{
        background: `linear-gradient(135deg, ${colors.panel2} 0%, rgba(102, 126, 234, 0.15) 100%)`,
        border: `2px solid ${colors.primary}`,
        padding: `${spacing.s4}`,
        borderRadius: radius.lg,
        marginBottom: spacing.s3,
        boxShadow: shadows.glow
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3,
        marginBottom: spacing.s3
      }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: gradients.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: shadows.glow
        }}>
          <Zap size={22} color={colors.text} />
        </div>
        
        <div>
          <div style={{
            fontSize: typography.h3.fontSize,
            fontWeight: '700',
            color: colors.text,
            marginBottom: '2px'
          }}>
            Your MIQ Baseline is Ready
          </div>
          <div style={{
            fontSize: typography.bodySmall.fontSize,
            color: colors.textSecondary
          }}>
            View your personalized intelligence report.
          </div>
        </div>
      </div>
      
      {/* Action Button */}
      <button
        data-testid={isGeniusTier ? "view-baseline-report-btn" : "upgrade-to-genius-btn"}
        onClick={handleAction}
        style={{
          width: '100%',
          padding: `${spacing.s3} ${spacing.s4}`,
          background: isGeniusTier ? gradients.primary : gradients.gold,
          border: 'none',
          borderRadius: radius.md,
          color: colors.text,
          fontFamily: typography.fontFamily,
          fontSize: typography.body.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: spacing.s2,
          boxShadow: isGeniusTier ? shadows.glow : '0 4px 15px rgba(255, 193, 7, 0.3)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {isGeniusTier ? (
          <>
            <Sparkles size={18} />
            View Baseline Report
          </>
        ) : (
          <>
            <Lock size={16} />
            Upgrade to Genius Plaite
          </>
        )}
      </button>
      
      {/* Genius benefit hint for non-Genius users */}
      {!isGeniusTier && (
        <div style={{
          marginTop: spacing.s2,
          fontSize: '11px',
          color: colors.textSecondary,
          textAlign: 'center'
        }}>
          Unlock your full metabolic intelligence profile
        </div>
      )}
    </div>
  );
};

/**
 * Combined MIQ Banner Component
 * Decides which banner to show based on calibration state
 */
const MIQCalibrationBanner = ({
  calibrationDay,
  isCalibrating,
  calibrationComplete,
  baselineViewedAt,
  isGeniusTier,
  isSignupTrial,
  onNavigateToMIQ,
  onViewBaseline,
  onUpgrade
}) => {
  // MIQ Baseline callout removed - no longer showing "Your MIQ Baseline is Ready" card
  
  // Countdown banner: Show only on Day 25-29 (when still calibrating)
  if (isCalibrating && calibrationDay >= 25 && calibrationDay <= 29) {
    const daysRemaining = 30 - calibrationDay;
    return (
      <MIQCountdownBanner
        daysRemaining={daysRemaining}
        onNavigateToMIQ={onNavigateToMIQ}
        isSignupTrial={isSignupTrial}
      />
    );
  }
  
  return null;
};

export default MIQCalibrationBanner;
